import React, { useCallback, useEffect, useRef } from 'react';
import { useLoadingSpinnerStore } from 'src/store/util/loadingSpinnerUtil';
import { showWarning, ShowSuccessFetch } from 'src/utils/toast/toastUtil';
import { formatDate } from 'src/utils/string/stringUtil';
import { AgGridReact } from 'ag-grid-react';
import useFetchActionbarAuth from 'src/hooks/useFetchActionbarAuth';
import { gridRefSelectedRowHandler } from 'src/utils/grid/GridHandlerContent';
import {
	getTQC3020_R01SalesList,
	getTQC3020_R01SalesAllCount,
	getTQC3020_R01SalesLotList,
	getTQC3020_R01ProductList,
	getTQC3020_R01ProductLotList,
	getTQC3020_R01ProductionHistory,
	getTQC3020_R01MaterialConsumption,
	getTQC3020_R01MaterialInbound,
} from "./Api_TQC3020_R01";
import {
	TQC3020_R01SalesDto,
	TQC3020_R01SalesLotDto,
	TQC3020_R01ProductBaseDto,
	TQC3020_R01ProductLotDto,
	TQC3020_R01ProductResultDto,
	TQC3020_R01BomDto,
} from './Dto_TQC3020_R01';
import { useTQC3020_R01Store } from './Store_TQC3020_R01';

interface Props {
	salesGridRef: React.RefObject<AgGridReact<TQC3020_R01SalesDto> | null>;
	productGridRef: React.RefObject<AgGridReact<TQC3020_R01ProductBaseDto> | null>;
	setMessage: (message: string) => void;
}

export const Content_TQC3020_R01 = (props: Props) => {
	const {
		salesGridRef,
		productGridRef,
		setMessage,
	} = props;

	const {
		TQC3020_R01activeTab: activeTab,
		TQC3020_R01Filter,
		TQC3020_R01dataList: dataList,
		setTQC3020_R01SalesList: setSalesList,
		setSelectedTQC3020_R01Sales: setSelectedSales,
		setTQC3020_R01SalesLotList: setSalesLotList,
		setSelectedTQC3020_R01SalesLot: setSelectedSalesLot,
		setTQC3020_R01SalesAllCount: setSalesAllCount,
		TQC3020_R01salesAllCount: salesAllCount,
		TQC3020_R01productBaseList: productBaseList,
		setTQC3020_R01ProductBaseList: setProductBaseList,
		setSelectedTQC3020_R01ProductBase: setSelectedProductBase,
		setTQC3020_R01ProductLotList: setProductLotList,
		setSelectedTQC3020_R01ProductLot: setSelectedProductLot,
		setTQC3020_R01ProductResultList: setProductResultList,
		setSelectedTQC3020_R01ProductResult: setSelectedProductResult,
		setTQC3020_R01BomList: setBomList,
		setSelectedTQC3020_R01Bom: setSelectedBom,
		setTQC3020_R01StockList: setStockList,
		resetAllTQC3020ScrollTop,
		resetTQC3020ScrollTop,
	} = useTQC3020_R01Store();

	const { setLoadingSpinner } = useLoadingSpinnerStore();
	
	// 권한 정보 로드
	useFetchActionbarAuth("TQC3020_R01");

	// 매출기준 조회
	const fetchSalesData = useCallback(async () => {
		setLoadingSpinner(true);
		resetAllTQC3020ScrollTop();
		try {
			const params = {
				startDate: TQC3020_R01Filter.salesDateRange?.from ? formatDate(TQC3020_R01Filter.salesDateRange.from) : undefined,
				endDate: TQC3020_R01Filter.salesDateRange?.to ? formatDate(TQC3020_R01Filter.salesDateRange.to) : undefined,
				compCdCondition: TQC3020_R01Filter.salesCompCdCondition || undefined,
				customCdCondition: TQC3020_R01Filter.salesCustomCdCondition || undefined,
				itemCdCondition: TQC3020_R01Filter.salesItemCd || undefined,
				listLength: dataList.length || 0,
			};

		const response = await getTQC3020_R01SalesList('search', params);
		const newList = response.data.data || [];
		setSalesList(newList);

		const countResponse = await getTQC3020_R01SalesAllCount(params);
		const totalCount = countResponse.data.data || 0;
		setSalesAllCount(totalCount);

		if (newList.length > 0) {
			// 첫 번째 행 자동 선택 (UI)
			gridRefSelectedRowHandler(
				salesGridRef,
				setSelectedSales,
				() => true,
			);

			const firstRow = newList[0];
			// 첫 행 기준 연쇄 조회: Lot → 생산정보 → BOM → 입고이력
			const lotRes = await getTQC3020_R01SalesLotList(firstRow.salesBillNo);
			const lotList: TQC3020_R01SalesLotDto[] = lotRes.data.data || [];
			setSalesLotList(lotList);
			// 선택 행 하이라이트용 valueIndex 부여 (Store와 동일하게 1부터)
			setSelectedSalesLot(lotList.length > 0 ? { ...lotList[0], valueIndex: 1 } : undefined);

			if (lotList.length > 0) {
				const prRes = await getTQC3020_R01ProductionHistory(lotList[0].lotNo);
				const productResultList: TQC3020_R01ProductResultDto[] = prRes.data.data || [];
				setProductResultList(productResultList);
				setSelectedProductResult(productResultList.length > 0 ? productResultList[0] : undefined);

				if (productResultList.length > 0) {
					const bomRes = await getTQC3020_R01MaterialConsumption(productResultList[0].productResultNo);
					const bomListData: TQC3020_R01BomDto[] = bomRes.data.data || [];
					setBomList(bomListData);
					setSelectedBom(bomListData.length > 0 ? { ...bomListData[0], valueIndex: 1 } : undefined);

					if (bomListData.length > 0) {
						const stockRes = await getTQC3020_R01MaterialInbound(bomListData[0].lotNo);
						setStockList(stockRes.data.data || []);
					} else {
						setStockList([]);
					}
				} else {
					setBomList([]);
					setSelectedBom(undefined);
					setStockList([]);
				}
			} else {
				setProductResultList([]);
				setSelectedProductResult(undefined);
				setBomList([]);
				setSelectedBom(undefined);
				setStockList([]);
			}

			ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${totalCount}건)`);
			setTimeout(() => setMessage(""), 5000);
		} else {
			showWarning('데이터가 없습니다.');
			setSelectedSales(undefined);
			setSalesLotList([]);
			setSelectedSalesLot(undefined);
			setProductResultList([]);
			setSelectedProductResult(undefined);
			setBomList([]);
			setSelectedBom(undefined);
			setStockList([]);
		}
		} catch (error) {
			// console.error('매출기준 조회 오류:', error);
			showWarning('조회 중 오류가 발생했습니다.');
		} finally {
			setLoadingSpinner(false);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [TQC3020_R01Filter, dataList.length, setSalesList, setSelectedSales, setSalesLotList, setSelectedSalesLot, setSalesAllCount, setProductResultList, setSelectedProductResult, setBomList, setSelectedBom, setStockList, setMessage, setLoadingSpinner, salesGridRef, resetAllTQC3020ScrollTop]);

	// 생산기준 조회
	const fetchProductData = useCallback(async () => {
		setLoadingSpinner(true);
		resetAllTQC3020ScrollTop();
		try {
			const params = {
				startDate: TQC3020_R01Filter.productDateRange?.from ? formatDate(TQC3020_R01Filter.productDateRange.from) : undefined,
				endDate: TQC3020_R01Filter.productDateRange?.to ? formatDate(TQC3020_R01Filter.productDateRange.to) : undefined,
				compCdCondition: TQC3020_R01Filter.productCompCdCondition || undefined,
				itemCdCondition: TQC3020_R01Filter.productItemCdCondition || undefined,
				workplaceCdCondition: TQC3020_R01Filter.productWorkplaceCdCondition || undefined,
				listLength: productBaseList.length || 0,
			};

		const response = await getTQC3020_R01ProductList('search', params);
		const newList = response.data.data || [];
		setProductBaseList(newList);

		if (newList.length > 0) {
			// 첫 번째 행 자동 선택 (UI)
			gridRefSelectedRowHandler(
				productGridRef,
				setSelectedProductBase,
				() => true,
			);

			const firstRow = newList[0];
			if (!firstRow.productResultNo) {
				setProductLotList([]);
				setSelectedProductLot(undefined);
				setProductResultList([]);
				setSelectedProductResult(undefined);
				setBomList([]);
				setSelectedBom(undefined);
				setStockList([]);
			} else {
				// 첫 행 기준 연쇄 조회: Lot → 생산정보 → BOM → 입고이력
				const lotRes = await getTQC3020_R01ProductLotList(firstRow.productResultNo);
				const lotList: TQC3020_R01ProductLotDto[] = lotRes.data.data || [];
				setProductLotList(lotList);
				setSelectedProductLot(lotList.length > 0 ? { ...lotList[0], valueIndex: 1 } : undefined);

				if (lotList.length > 0) {
					const prRes = await getTQC3020_R01ProductionHistory(lotList[0].lotNo);
					const productResultList: TQC3020_R01ProductResultDto[] = prRes.data.data || [];
					setProductResultList(productResultList);
					setSelectedProductResult(productResultList.length > 0 ? productResultList[0] : undefined);

					if (productResultList.length > 0) {
						const bomRes = await getTQC3020_R01MaterialConsumption(productResultList[0].productResultNo);
						const bomListData: TQC3020_R01BomDto[] = bomRes.data.data || [];
						setBomList(bomListData);
						setSelectedBom(bomListData.length > 0 ? { ...bomListData[0], valueIndex: 1 } : undefined);

						if (bomListData.length > 0) {
							const stockRes = await getTQC3020_R01MaterialInbound(bomListData[0].lotNo);
							setStockList(stockRes.data.data || []);
						} else {
							setStockList([]);
						}
					} else {
						setBomList([]);
						setSelectedBom(undefined);
						setStockList([]);
					}
				} else {
					setProductResultList([]);
					setSelectedProductResult(undefined);
					setBomList([]);
					setSelectedBom(undefined);
					setStockList([]);
				}
			}

			ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}건)`);
			setTimeout(() => setMessage(""), 5000);
		} else {
			showWarning('데이터가 없습니다.');
			setSelectedProductBase(undefined);
			setProductLotList([]);
			setSelectedProductLot(undefined);
			setProductResultList([]);
			setSelectedProductResult(undefined);
			setBomList([]);
			setSelectedBom(undefined);
			setStockList([]);
		}
		} catch (error) {
			// console.error('생산기준 조회 오류:', error);
			showWarning('조회 중 오류가 발생했습니다.');
		} finally {
			setLoadingSpinner(false);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [TQC3020_R01Filter, productBaseList.length, setProductBaseList, setSelectedProductBase, setProductLotList, setSelectedProductLot, setProductResultList, setSelectedProductResult, setBomList, setSelectedBom, setStockList, setMessage, setLoadingSpinner, productGridRef, resetAllTQC3020ScrollTop]);

	// 매출 행 클릭 시 Lot 조회
	const handleSalesRowClick = useCallback(async (data: TQC3020_R01SalesDto) => {
		resetTQC3020ScrollTop(['salesLot', 'productResult', 'bom', 'stock']);
		if (!data.salesBillNo) {
			showWarning('매출전표번호가 없습니다.');
			setSalesLotList([]);
			setSelectedSalesLot(undefined);
			setProductResultList([]);
			setBomList([]);
			setSelectedBom(undefined);
			setStockList([]);
			return;
		}
		
		try {
			// 1. 매출기준 Lot 조회 (상단 Lot 테이블용)
			const lotResponse = await getTQC3020_R01SalesLotList(data.salesBillNo);
			const lotList: TQC3020_R01SalesLotDto[] = lotResponse.data.data || [];
			setSalesLotList(lotList);

			// 매출 Lot 선택 상태 초기화
			setSelectedSalesLot(undefined);

			// 하단 테이블은 Lot 클릭 시 조회되도록 초기화
			setProductResultList([]);
			setBomList([]);
			setSelectedBom(undefined);
			setStockList([]);
			
		} catch (error) {
			setSalesLotList([]);
			setSelectedSalesLot(undefined);
			setProductResultList([]);
			setBomList([]);
			setSelectedBom(undefined);
			setStockList([]);
		}
	}, [setSalesLotList, setSelectedSalesLot, setProductResultList, setBomList, setSelectedBom, setStockList, resetTQC3020ScrollTop]);

	// 생산 행 클릭 시 Lot 조회
	const handleProductRowClick = useCallback(async (data: TQC3020_R01ProductBaseDto) => {
		resetTQC3020ScrollTop(['productLot', 'productResult', 'bom', 'stock']);
		if (!data.productResultNo) {
			showWarning('생산실적번호가 없습니다.');
			setProductLotList([]);
			setSelectedProductLot(undefined);
			setProductResultList([]);
			setBomList([]);
			setSelectedBom(undefined);
			setStockList([]);
			return;
		}
		
		try {
			// 1. 생산 Lot 조회 (상단 Lot 테이블용) - productResultNo 사용
			const productLotResponse = await getTQC3020_R01ProductLotList(data.productResultNo);
			const lotList: TQC3020_R01ProductLotDto[] = productLotResponse.data.data || [];
			setProductLotList(lotList);

			// 생산 Lot 선택 상태 초기화
			setSelectedProductLot(undefined);

			// 하단 테이블 초기화 (LOT 생산정보 행 클릭 시 조회됨)
			setProductResultList([]);
			setBomList([]);
			setSelectedBom(undefined);
			setStockList([]);
			
		} catch (error) {
			setProductLotList([]);
			setSelectedProductLot(undefined);
			setProductResultList([]);
			setBomList([]);
			setSelectedBom(undefined);
			setStockList([]);
		}
	}, [setBomList, setProductResultList, setProductLotList, setSelectedProductLot, setStockList, setSelectedBom, resetTQC3020ScrollTop]);

	// BOM 행 클릭 시 Lot 입고 근거 조회
	const handleBomRowClick = useCallback(async (data: TQC3020_R01BomDto) => {
		resetTQC3020ScrollTop(['stock']);
		if (!data.lotNo) {
			showWarning('Lot 번호가 없습니다.');
			setStockList([]);
			return;
		}
		
		try {
			// Lot 번호 기준으로 입고 근거 조회
			const stockResponse = await getTQC3020_R01MaterialInbound(data.lotNo);
			setStockList(stockResponse.data.data || []);
		} catch (error) {
			setStockList([]);
		}
	}, [setStockList, resetTQC3020ScrollTop]);

	// ========================================
	// 매출 Lot 클릭 시 생산 정보 조회
	// ========================================
	const handleSalesLotRowClick = useCallback(async (data: TQC3020_R01SalesLotDto) => {
		resetTQC3020ScrollTop(['productResult', 'bom', 'stock']);
		if (!data.lotNo) {
			setProductResultList([]);
			setBomList([]);         // BOM 리스트도 초기화
			setStockList([]);       // 입고이력도 초기화
			return;
		}
		
		try {
			// 1️⃣ Lot 번호로 생산 이력 조회
			const productResultResponse = await getTQC3020_R01ProductionHistory(data.lotNo);
			const productResultList = productResultResponse.data.data || [];
			setProductResultList(productResultList);
			
			// 2️⃣ 하단 테이블 초기화 (LOT 생산정보 행 클릭 시 조회됨)
			setBomList([]);
			setSelectedBom(undefined);
			setStockList([]);
			
		} catch (error) {
			// 에러 발생 시 모든 하단 테이블 초기화
			setProductResultList([]);
			setBomList([]);
			setStockList([]);
		}
	}, [setProductResultList, setBomList, setStockList, setSelectedBom, resetTQC3020ScrollTop]);

	// 생산 Lot 클릭 시 생산 정보 조회
	const handleProductLotRowClick = useCallback(async (data: TQC3020_R01ProductLotDto) => {
		resetTQC3020ScrollTop(['productResult', 'bom', 'stock']);
		if (!data.lotNo) {
			setProductResultList([]);
			return;
		}
		
		try {
			// Lot 번호로 생산 이력 조회
			const productResultResponse = await getTQC3020_R01ProductionHistory(data.lotNo);
			setProductResultList(productResultResponse.data.data || []);
		} catch (error) {
			setProductResultList([]);
		}
	}, [setProductResultList, resetTQC3020ScrollTop]);

	// LOT 생산정보 행 클릭 시 자재 투입 내역 조회
	const handleProductResultRowClick = useCallback(async (data: TQC3020_R01ProductResultDto) => {
		resetTQC3020ScrollTop(['bom', 'stock']);
		if (!data.productResultNo) {
			setBomList([]);
			setStockList([]);
			return;
		}
		
		try {
			// 생산실적번호로 자재 투입 내역 조회
			const bomResponse = await getTQC3020_R01MaterialConsumption(data.productResultNo);
			setBomList(bomResponse.data.data || []);
			setSelectedBom(undefined);
			
			// 입고이력은 자재 행 클릭 시 조회되므로 초기화
			setStockList([]);
		} catch (error) {
			setBomList([]);
			setStockList([]);
		}
	}, [setBomList, setStockList, setSelectedBom, resetTQC3020ScrollTop]);

	// 탭별 조회
	const fetchData = useCallback(async () => {
		if (activeTab === 'sales') {
			await fetchSalesData();
		} else {
			await fetchProductData();
		}
	}, [activeTab, fetchSalesData, fetchProductData]);

	// 탭 변경 시 자동 조회 (초기 마운트 제외)
	const isInitialMount = useRef(true);
	useEffect(() => {
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}
		fetchData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeTab]);

	return {
		fetchData,
		handleSalesRowClick,
		handleProductRowClick,
		handleSalesLotRowClick,
		handleProductLotRowClick,
		handleProductResultRowClick,
		handleBomRowClick,
	};
};

